import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import './CustomSelect.scss';
import Icon from '../Icon/Icon';

const CustomSelect = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  name,
  required,
  searchable,
  searchPlaceholder,
  error,
  loading,
  loadingText,
  emptyText,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(value || '');
  const [searchQuery, setSearchQuery] = useState('');
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setSelectedOption(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option => 
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  const handleSelect = (option) => {
    setSelectedOption(option.value);
    onChange(option.value);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
    if (isOpen) setSearchQuery('');
  };

  const selectedLabel = options.find(opt => opt.value === selectedOption)?.label || placeholder;

  return (
    <div 
      className={`custom-select ${className || ''} ${disabled ? 'disabled' : ''} ${error ? 'has-error' : ''} ${isOpen ? 'is-open' : ''}`}
      ref={selectRef}
    >
      <div 
        className={`select-header ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
          if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchQuery('');
          }
        }}
      >
        <span className={`selected-value ${!selectedOption ? 'placeholder' : ''}`}>
          {loading ? loadingText : selectedLabel}
        </span>
        <Icon 
          alias="chevron-down" 
          className={`select-chevron ${isOpen ? 'rotated' : ''}`} 
          width={16} 
          height={16} 
          color="#1C1B1F" 
        />
      </div>
      
      {isOpen && (
        <div className="select-dropdown">
          {searchable && (
            <div className="select-search">
              <Icon alias="search" width={16} height={16} color="#94a3b8" className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="select-options">
            {filteredOptions.length === 0 ? (
              <div className="select-empty">
                {searchQuery ? 'No results found' : emptyText}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`select-option ${option.value === selectedOption ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={option.value === selectedOption}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      <input
        type="hidden"
        name={name}
        value={selectedOption}
        required={required}
      />
    </div>
  );
};

CustomSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  required: PropTypes.bool,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  error: PropTypes.bool,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  emptyText: PropTypes.string,
};

CustomSelect.defaultProps = {
  placeholder: 'Select an option',
  disabled: false,
  required: false,
  searchable: false,
  searchPlaceholder: 'Search...',
  error: false,
  loading: false,
  loadingText: 'Loading...',
  emptyText: 'No options available',
};

export default CustomSelect; 